import { useCallback, useEffect, useState } from "react";
import client, { apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { ClipboardCheck, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { ChecklistFill } from "@/components/forms/ChecklistFill";
import { ChecklistPicker } from "@/components/forms/ChecklistPicker";
import { ChecklistEmailDialog, openChecklistPdf } from "@/components/forms/ChecklistEmailDialog";
import { FileDown, Mail, Download } from "lucide-react";

const STATUS = { assigned: ["Not started", "bg-slate-100 text-slate-600"], in_progress: ["In progress", "bg-amber-50 text-amber-700 border-amber-200"], submitted: ["Completed", "bg-emerald-50 text-emerald-700 border-emerald-200"] };

// Checklists attached to one job visit: fill in, and (managers) change which ones apply.
export const JobChecklists = ({ jobId, isSeries, canManage }) => {
  const [data, setData] = useState({ templateIds: [], instances: [] });
  const [fill, setFill] = useState(null);
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState([]);
  const [pick, setPick] = useState([]);
  const [series, setSeries] = useState(true);
  const [emailing, setEmailing] = useState(null);
  const [req, setReq] = useState(false);
  const load = useCallback(() => { client.get(`/forms/job/${jobId}`).then((r) => setData(r.data)).catch(() => {}); }, [jobId]);
  useEffect(() => { load(); }, [load]);
  const startEdit = () => { client.get("/forms/published").then((r) => setOptions(r.data)).catch(() => {}); setPick(data.templateIds); setReq(!!data.required); setEditing(true); };
  const saveEdit = async () => {
    try {
      const { data: r } = await client.put(`/forms/job/${jobId}/checklists`, { templateIds: pick, required: req, applyToSeries: isSeries && series });
      toast.success(`Checklists updated on ${r.visits} visit${r.visits === 1 ? "" : "s"}`); setEditing(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-4 border-slate-200 space-y-3" data-testid="job-checklists">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-900 flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-brand" />Checklists
          {data.required && data.instances.length > 0 && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 font-normal" data-testid="job-checklists-required-badge"><Lock className="h-3 w-3 mr-1" />Must be completed</Badge>}</p>
        {canManage && !editing && <Button size="sm" variant="outline" onClick={startEdit} data-testid="job-checklists-edit"><Pencil className="h-3.5 w-3.5 mr-1" />Change</Button>}
      </div>
      {editing ? (
        <div className="space-y-3">
          <ChecklistPicker options={options} value={pick} onChange={setPick} testid="job-checklists-picker" />
          <label className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer">
            <span><span className="block text-sm font-medium text-slate-800">Must be completed before the job can be finished</span>
              <span className="block text-xs text-slate-500">Cleaners can't end this job in the app until its checklists are submitted.</span></span>
            <Switch checked={req} onCheckedChange={setReq} data-testid="job-checklists-required" />
          </label>
          {isSeries && <label className="flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={series} onCheckedChange={(v) => setSeries(!!v)} data-testid="job-checklists-series" />Also apply to all future visits of this job</label>}
          <div className="flex gap-2"><Button size="sm" onClick={saveEdit} className="bg-brand hover:bg-brand-dark" data-testid="job-checklists-save">Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button></div>
        </div>
      ) : data.instances.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="job-checklists-none">No checklists on this job.</p>
      ) : (
        <div className="space-y-2">
          {data.instances.map((i) => {
            const [label, cls] = STATUS[i.status] || STATUS.assigned;
            return (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2" data-testid={`job-checklist-${i.id}`}>
                <div className="min-w-0 flex items-center gap-2">{i.enforce && <Lock className="h-3.5 w-3.5 text-slate-400" title="Must be completed before finishing" />}<span className="truncate text-sm font-medium text-slate-800">{i.name}</span></div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline" className={cls}>{label}</Badge>
                  <Button size="sm" variant="outline" onClick={() => setFill(i)} data-testid={`job-checklist-open-${i.id}`}>{i.status === "submitted" ? "View" : "Fill in"}</Button>
                  {canManage && i.status === "submitted" && <>
                    <Button size="sm" variant="ghost" title="Preview PDF" onClick={() => openChecklistPdf(i.id)} data-testid={`job-checklist-pdf-${i.id}`}><FileDown className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" title="Download PDF" onClick={() => openChecklistPdf(i.id, `${i.name}.pdf`)} data-testid={`job-checklist-download-${i.id}`}><Download className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => setEmailing(i.id)} className="text-brand border-brand/40" data-testid={`job-checklist-email-${i.id}`}><Mail className="h-3.5 w-3.5 mr-1" />{i.emailedAt ? "Email again" : "Email client"}</Button>
                  </>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ChecklistFill inst={fill} onOpenChange={(o) => !o && setFill(null)} onSaved={load} />
      <ChecklistEmailDialog iid={emailing} onOpenChange={(o) => !o && setEmailing(null)} onSent={load} />
    </Card>
  );
};
